import React, { useCallback, useEffect, useState } from 'react';

import {
  buscarRetrabalhoOficina,
  FiltrosOficina,
  RespostaRetrabalho,
} from '../../../api/dashboardOficinaApi';
import { notify } from '../../../shared/feedback/notify';
import { formatarNumero } from '../../../shared/ui/chart-palette';
import DetalheTabelaModal from '../../../shared/ui/DetalheTabelaModal';
import { AreaEstado, CardGrafico, Kpi } from '../../../shared/ui/GraficoBase';
import TabelaPaginada from '../../../shared/ui/TabelaPaginada';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/feedback/aviso-de-tela.css';

/**
 * Retrabalho (at-133): dos veículos entregues, quantos voltaram em garantia (pós-evento).
 *
 * ⚠️ Carga PRÓPRIA, independente da Visão Geral: a falha deste card não pode derrubar os
 * indicadores da aba, e a falha da aba não pode apagar este card.
 *
 * ⚠️ Percentual SEM semáforo de cor: não existe limiar decidido de "retrabalho aceitável", e
 * pintar de verde ou vermelho inventaria um.
 */

interface Props {
  filtros: FiltrosOficina;
  sidebarRef?: React.RefObject<HTMLElement>;
}

const dataBr = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—';

const pct = (valor: number | null) =>
  valor === null ? '—' : `${valor.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

const RetrabalhoCard: React.FC<Props> = ({ filtros, sidebarRef }) => {
  const [dados, setDados] = useState<RespostaRetrabalho | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [listaAberta, setListaAberta] = useState(false);

  const carregar = useCallback(async (f: FiltrosOficina) => {
    setCarregando(true);
    try {
      const resposta = await buscarRetrabalhoOficina(f);
      if (!resposta || !Array.isArray(resposta.porEsteira)) {
        throw new Error('Resposta do servidor em formato inesperado');
      }
      setDados(resposta);
      setErro(null);
    } catch (e) {
      setDados(null);
      setErro(
        'Não foi possível carregar o retrabalho. Enquanto este aviso estiver na tela, não há número de retorno para ler aqui — recarregue a página; se persistir, acione a TI.',
      );
      notify.errorFrom(
        e,
        'Não foi possível carregar o retrabalho. Recarregue a página; se persistir, acione a TI.',
        { toastId: 'dashboard-oficina-retrabalho-falhou' },
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar(filtros);
  }, [filtros, carregar]);

  const respondeu = dados !== null;
  const ignorouSituacao = dados?.filtrosNaoAplicados.includes('situacao') ?? false;

  return (
    <div className="do-grid-largo">
      <CardGrafico
        titulo="Retrabalho"
        descricao="Dos veículos entregues, quantos voltaram em garantia (pós-evento). Um retorno conta em todas as etapas pelas quais o serviço passou: o sistema não sabe qual delas falhou."
      >
        {erro && (
          <div className="erro-de-tela" role="alert">
            {erro}
          </div>
        )}

        {ignorouSituacao && (
          <div className="aviso-de-tela">
            Este card olha só os veículos entregues, independente do filtro de situação: só quem
            saiu da oficina pode voltar.
          </div>
        )}

        <div className="do-kpis">
          <Kpi
            rotulo="Voltaram em garantia"
            valor={respondeu ? pct(dados.percentual) : '-'}
            apoio={
              !respondeu
                ? 'Dos veículos entregues'
                : dados.totalServicos === 0
                  ? 'Nenhuma entrega no recorte'
                  : `${formatarNumero(dados.servicosComRetorno)} de ${formatarNumero(dados.totalServicos)} entregues`
            }
            carregando={carregando && !respondeu}
            onDetalhar={respondeu && dados.retornos.length > 0 ? () => setListaAberta(true) : undefined}
            detalheRotulo="Ver os veículos que voltaram"
          />
        </div>

        {!respondeu || dados.porEsteira.length === 0 ? (
          <div className="do-plot">
            <AreaEstado
              carregando={carregando && !respondeu}
              mensagem={
                respondeu
                  ? 'Nenhuma etapa de trabalho concluída pelos veículos entregues neste recorte.'
                  : 'Não foi possível carregar — veja o aviso acima.'
              }
            />
          </div>
        ) : (
          <TabelaPaginada
            itens={dados.porEsteira}
            chave={(l) => l.esteiraId}
            vazio="Nenhuma etapa neste recorte."
            colunas={[
              { titulo: 'Esteira', largura: 220, texto: (l) => l.esteira },
              { titulo: 'Entregues', largura: 120, texto: (l) => formatarNumero(l.servicos) },
              { titulo: 'Voltaram', largura: 120, texto: (l) => formatarNumero(l.comRetorno) },
              { titulo: 'Retrabalho', largura: 120, texto: (l) => pct(l.percentual) },
            ]}
          />
        )}
      </CardGrafico>

      {listaAberta && dados && (
        <DetalheTabelaModal
          titulo="Veículos que voltaram em garantia"
          apoio={
            dados.listaCortada
              ? `Mostrando ${formatarNumero(dados.retornos.length)} de ${formatarNumero(dados.totalRetornos)} retornos, dos mais recentes para os mais antigos.`
              : 'Retornos dos mais recentes para os mais antigos.'
          }
          itens={dados.retornos}
          chave={(r) => `${r.placa}-${r.voltouEm}`}
          vazio="Nenhum retorno neste recorte."
          colunas={[
            { titulo: 'Placa', largura: 110, texto: (r) => r.placa },
            { titulo: 'Entregue em', largura: 120, texto: (r) => dataBr(r.servicoEntregueEm) },
            { titulo: 'Voltou em', largura: 120, texto: (r) => dataBr(r.voltouEm) },
            {
              titulo: 'Dias até voltar',
              largura: 130,
              texto: (r) => (r.diasAteVoltar === null ? '—' : `${r.diasAteVoltar} dias`),
            },
            { titulo: 'Etapas do serviço', largura: 260, texto: (r) => r.esteiras.join(', ') || '—' },
          ]}
          onFechar={() => setListaAberta(false)}
          sidebarRef={sidebarRef}
        />
      )}
    </div>
  );
};

export default RetrabalhoCard;
